<template>
  <div class="mr-3" v-if="section">
    <NavigationBreadcrumb
      :items="breadcrumb_items"
      :sectionId="section_id"
      :showMatterProgram="true"
      :showPdaView="true"
      :showAssessmentStudents="true"
      :showEvaluatorEvaluations="true"
      :showAssessmentRangeSectionView="true"
      :showProfessorProgressionReportView="true"
    ></NavigationBreadcrumb>
    <div class="ml-3">
      <div class="mt-3 mb-2">
        <template v-if="section.egress_profile_matter != null">
          <h4>
            {{
              $t("dashboard.dashboardView.informationOfThe", {
                entity: $getVisibleNames(
                  "mesh.egressprofilematter",
                  false,
                  "Asignatura"
                ),
              })
            }}
          </h4>
          <NewMatterPresentation
            class="mt-3"
            v-if="egress_profile_matter && matter"
            :Matter="egress_profile_matter"
            :oldMatter="matter"
          ></NewMatterPresentation>
        </template>
        <template v-else-if="section.matter != null">
          <h4>
            {{
              $t("dashboard.dashboardView.informationOfThe", {
                entity: $getVisibleNames(
                  "teaching.matter",
                  false,
                  "Asignatura Base"
                ),
              })
            }}
          </h4>
          <NewMatterPresentation
            class="mt-3"
            v-if="matter"
            :oldMatter="matter"
          ></NewMatterPresentation>
        </template>
      </div>
      <div class="mb-4">
        <h2>
          {{
            $t("dashboard.dashboardView.gradesRecordOfThe", {
              entity: $getVisibleNames("teaching.section", false, "Sección"),
            })
          }}:
          <strong>
            {{ section.name }}
          </strong>
        </h2>
      </div>
      <div class="flex-container">
        <div class="search noprint m-1 p-2 w-100">
          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <b-icon icon="search"></b-icon>
            </b-input-group-prepend>
            <b-form-input
              id="search-form"
              type="search"
              v-model="input_search"
              :placeholder="$t('dashboard.dashboardView.searchStudents')"
            >
            </b-form-input>
            <b-input-group-append>
              <b-button :disabled="!input_search" @click="input_search = ''">{{
                $t("reusable.common.clear")
              }}</b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div v-if="evaluationTypesList.length > 0" class="w-50">
          <v-select
            class="mt-2 v-select-class"
            id="select-selected_types"
            :placeholder="
              $t('evaluations.newEvaluations.selectOneOrMoreToFilter')
            "
            :options="evaluationTypesList"
            v-model="selected_types"
            :reduce="(evaluationTypesList) => evaluationTypesList.id"
            :selectable="(option) => !selected_types.includes(option.id)"
            label="value"
            track-by="id"
            size="sm"
            multiple
            :dropdown-should-open="dropdownShouldOpen"
          >
            <template v-slot:no-options="{ search, searching }">
              <template v-if="searching">
                {{ $t("evaluations.newEvaluations.noResultsFoundFor") }}
                <em>{{ search }}</em
                >.
              </template>
            </template>
          </v-select>
        </div>
        <div class="column-selector noprint" v-if="displayList.length > 0">
          <b-button
            :id="`col-display-1`"
            class="col-display-1"
            variant="none"
            size="sm"
            v-b-tooltip.v-secondary.noninteractive.hover="
              $t('dashboard.dashboardView.viewColumns')
            "
          >
            <b-icon icon="eye"></b-icon>
          </b-button>
          <b-popover
            :id="`columns-popover-1`"
            :target="`col-display-1`"
            placement="bottom"
            class="col-popover-1"
            triggers="click blur"
          >
            <template v-if="current_section != null">
              <template v-for="evaluation in filterEvaluation">
                <template
                  v-for="(instrument, index) in displayList.filter(
                    (x) => x.evaluation_id == evaluation.id
                  )"
                >
                  <div
                    :key="
                      'question_tests' +
                      instrument.index +
                      'evaluation' +
                      evaluation.id
                    "
                  >
                    <div v-if="index == 0">
                      <strong>
                        {{ evaluation.title }}
                      </strong>
                    </div>
                    <div class="column-check noprint">
                      <b-form-checkbox
                        :id="`column-check-${instrument.index}`"
                        name="checkbox-1"
                        :value="instrument.index"
                        v-model="check_instrument"
                      >
                        {{ instrument.title }}
                      </b-form-checkbox>
                    </div>
                  </div>
                </template>
              </template>
            </template>
          </b-popover>
        </div>
        <div class="noprint ml-1 d-flex align-items-center" v-if="current_section">
          <GenericButtonDownload
            :tooltip_text="$t('dashboard.dashboardView.downloadExcel')"
            :click_button="DownloadExcel"
            :disabled_button="disabled_button_excel"
            :icon_excel="true"
          ></GenericButtonDownload>
        </div>
      </div>

      <b-table-simple
        bordered
        responsive
        hover
        v-if="current_section"
        class="table-current-section mb-5"
      >
        <b-thead>
          <b-tr>
            <b-th rowspan="2" class="header-section align-content-center">
              <div>{{ $t("dashboard.dashboardView.student") }}</div>
            </b-th>
            <b-th
              v-for="evaluation in filterEvaluation"
              :key="evaluation.id"
              class="header-section align-content-center"
              @click="redirectEvaluatorView(evaluation.id)"
              :colspan="showColumn(evaluation.tests).length + 1"
            >
              <div>
                <div
                  class="div-href"
                  v-b-tooltip.v-secondary.noninteractive.hover="
                    $t('dashboard.dashboardView.goToEvaluation')
                  "
                >
                  <span class="underline-style">
                    {{ evaluation.title }} - {{ evaluation.weighing }}%</span
                  >
                </div>
              </div>
            </b-th>
            <b-th
              rowspan="2"
              class="header-section align-content-center"
              :class="{ 'final-grade': allows_crud }"
              ><div class="d-flex">
                {{ $t("dashboard.dashboardView.finalGrade") }}
                <b-icon
                  v-if="allows_crud && section"
                  class="ml-1 mt-1 style-icon-btn"
                  v-b-tooltip.v-secondary.noninteractive.hover="
                    $t(
                      'dashboard.dashboardView.pressToUpdateAutomaticScoreFinalGrade'
                    )
                  "
                  @click="updateAutomaticScoreAllSection"
                  icon="arrow-repeat"
                ></b-icon></div
            ></b-th>
          </b-tr>
          <b-tr>
            <template v-for="evaluation in filterEvaluation">
              <b-th
                class="header-section align-content-center"
                v-for="instrument in showColumn(evaluation.tests)"
                :key="evaluation.id + ':' + instrument.id"
                ><span :key="evaluation.id + ':' + instrument.id">
                  <span>
                    <span
                      class="div-href underline-style"
                      @click="
                        redirectEvaluatorView(evaluation.id, instrument.id)
                      "
                      v-b-tooltip.v-secondary.noninteractive.hover="
                        $t('dashboard.dashboardView.goToEvaluation')
                      "
                    >
                      {{ instrument.title }}
                    </span>
                    (<span
                      v-b-tooltip.v-secondary.noninteractive.hover="
                        $t('dashboard.dashboardView.maxScore') +
                        ': ' +
                        instrument.max_score
                      "
                      >{{ instrument.max_score | Round }}</span
                    >) -
                    <span
                      v-b-tooltip.v-secondary.noninteractive.hover="
                        $t('dashboard.dashboardView.weightingOfThe', {
                          entity: $getVisibleNames(
                            'evaluations2.test',
                            false,
                            'Instrumento'
                          ),
                        }) +
                        ': ' +
                        instrument.weighing
                      "
                      >{{ instrument.weighing | Round }}%</span
                    >
                  </span>
                  <b-icon
                    v-if="
                      can_publish_grades &&
                      instrument &&
                      !instrument.all_published
                    "
                    icon="cloud-arrow-up"
                    v-b-tooltip.right.hover.v-secondary.v-secondary="
                      $t('dashboard.dashboardView.pressToPublishScore', {
                        entity: $getVisibleNames(
                          'evaluations2.test',
                          false,
                          'Instrumento'
                        ),
                      })
                    "
                    @click="publishGradeToAllEvaluatees(instrument.id, true)"
                    class="style-icon-btn ml-1"
                  ></b-icon>
                  <b-icon
                    v-if="
                      can_publish_grades &&
                      instrument &&
                      instrument.published_count > 0
                    "
                    icon="cloud-download"
                    v-b-tooltip.right.hover.v-secondary.v-secondary="
                      $t('dashboard.dashboardView.pressToWithdrawScore', {
                        entity: $getVisibleNames(
                          'evaluations2.test',
                          false,
                          'Instrumento'
                        ),
                      })
                    "
                    @click="publishGradeToAllEvaluatees(instrument.id, false)"
                    class="style-icon-btn ml-1"
                  ></b-icon>
                  <b-icon
                    v-if="allows_crud && section"
                    class="ml-1 style-icon-btn"
                    v-b-tooltip.v-secondary.noninteractive.hover="
                      $t(
                        'dashboard.dashboardView.pressToUpdateAutomaticScoreColumn'
                      )
                    "
                    @click="updateAutomaticScoreAllTest(instrument.id)"
                    icon="arrow-repeat"
                  ></b-icon>
                </span>
              </b-th>
              <b-th
                class="header-section align-content-center"
                :key="evaluation.id"
              >
                <div class="d-flex">
                  {{ $t("dashboard.dashboardView.note") }}
                  <b-icon
                    v-if="allows_crud && section"
                    class="ml-1 mt-1 style-icon-btn"
                    v-b-tooltip.v-secondary.noninteractive.hover="
                      $t(
                        'dashboard.dashboardView.pressToUpdateAutomaticScoreColumn'
                      )
                    "
                    @click="updateAutomaticScoreAllEvaluation(evaluation.id)"
                    icon="arrow-repeat"
                  ></b-icon>
                </div>
              </b-th>
            </template>
          </b-tr>
        </b-thead>
        <b-tbody>
          <tr v-for="student in studentList" :key="student.student_id">
            <td class="text-left vertical-align-td">
              {{ student.first_name }} {{ student.last_name }}
            </td>
            <template v-for="evaluation in filterEvaluation">
              <template v-for="instrument in showColumn(evaluation.tests)">
                <NewFullGradingInputComponent
                  class="td-class vertical-align-td"
                  :key="
                    evaluation.id +
                    ':' +
                    instrument.id +
                    '-' +
                    (student_key_resets[student.id] || 0)
                  "
                  v-if="instrument.evaluatees.includes(student.id)"
                  :context="'test'"
                  :grade_ranges="grade_ranges"
                  :student="student"
                  :evaluatee_evaluations="student.evaluatee_evaluations"
                  :evaluation="evaluation"
                  :test="instrument"
                  :allows_crud="allows_crud"
                  :selected_range_group="selected_range_group"
                  @change_score="handleScoreChange"
                ></NewFullGradingInputComponent>
                <td
                  :key="evaluation.id + ':' + instrument.id"
                  v-b-tooltip.v-secondary.noninteractive.hover="
                    $t('dashboard.dashboardView.studentNotInEvaluatees', {
                      student: `${student.first_name} ${student.last_name}`,
                      entity: $getVisibleNames(
                        'evaluations2.test',
                        false,
                        'Instrumento'
                      ),
                    })
                  "
                  v-else
                >
                  <b-icon icon="person-x" scale="1.5"></b-icon>
                </td>
              </template>
              <!-- evaluation.evaluatees.includes(student.id) || -->
              <NewFullGradingInputComponent
                class="border-right vertical-align-td min-width-input"
                :key="
                  evaluation.id + '-' + (student_key_resets[student.id] || 0)
                "
                v-if="
                  getStudentIdsInTest(evaluation.tests).includes(student.id)
                "
                :context="'evaluation'"
                :grade_ranges="grade_ranges"
                :student="student"
                :evaluatee_evaluations="student.evaluatee_evaluations"
                :evaluation="evaluation"
                :allows_crud="allows_crud"
                :selected_range_group="selected_range_group"
                @change_score="handleScoreChange"
              ></NewFullGradingInputComponent>
              <td v-else :key="evaluation.id">
                <div
                  v-b-tooltip.v-secondary.noninteractive.hover="
                    $t('dashboard.dashboardView.studentNotInEvaluatees', {
                      student: `${student.first_name} ${student.last_name}`,
                      entity: $getVisibleNames(
                        'evaluations2.test',
                        false,
                        'Instrumento'
                      ),
                    })
                  "
                >
                  <b-icon icon="person-x" scale="1.5"></b-icon>
                </div>
              </td>
            </template>
            <!-- Nota Final -->
            <!-- <td
            v-if="student.section_grade == null"
            class="vertical-align-td"
            :style="{
              'background-color':
                !allows_crud && student.section_grade != null
                  ? gradeRangeBackground(institution.min_grade) + ' !important'
                  : '',
            }"
          >
            <template>
              {{ institution.min_grade }}
            </template>
          </td>
          <td
            v-else
            class="vertical-align-td"
            :style="{
              'background-color': !allows_crud
                ? gradeRangeBackground(getFinalGrade(student.section_grade)) +
                  ' !important'
                : '',
            }"
          >
            <template>
              {{ getFinalGrade(student.section_grade) }}
            </template>
          </td> -->
            <!-- Nota Final -->
            <td
              v-if="student && student.section_grade && !allows_crud"
              class="vertical-align-td"
              :style="{
                'background-color': !allows_crud
                  ? gradeRangeBackground(
                      student.section_grade.final_grade != null
                        ? parseFloat(
                            student.section_grade.final_grade.toFixed(1)
                          )
                        : student.section_grade.manual_grade != null
                        ? parseFloat(
                            student.section_grade.manual_grade.toFixed(1)
                          )
                        : student.section_grade.automated_grade != null
                        ? parseFloat(
                            student.section_grade.automated_grade.toFixed(1)
                          )
                        : institution.min_grade
                    ) + ' !important'
                  : '',
              }"
            >
              <span>
                {{
                  student.section_grade.final_grade != null
                    ? student.section_grade.final_grade.toFixed(1)
                    : student.section_grade.manual_grade != null
                    ? student.section_grade.manual_grade.toFixed(1)
                    : student.section_grade.automated_grade != null
                    ? student.section_grade.automated_grade.toFixed(1)
                    : institution.min_grade
                }}
              </span>
            </td>
            <!-- Celda donde no existe "student.section_grade" -->
            <td
              v-if="student && student.section_grade == null && !allows_crud"
              class="vertical-align-td"
              :style="{
                'background-color': !allows_crud
                  ? gradeRangeBackground(institution.min_grade) + ' !important'
                  : '',
              }"
            >
              <span>
                {{ institution.min_grade }}
              </span>
            </td>
            <!-- Celda con input donde no existe "student.section_grade" -->
            <td
              v-if="student && student.section_grade == null && allows_crud"
              class="text-center vertical-align-td"
            >
              <span>
                <div>
                  <b-form-input
                    :key="
                      student.id + '-' + (student_key_resets[student.id] || 0)
                    "
                    :ref="'student:' + student.id + 'section:' + section.id"
                    v-b-tooltip.v-secondary.noninteractive.hover="
                      isInputInvalid(student) ? getValidationMessage() : ''
                    "
                    size="sm"
                    step="0.1"
                    :max="institution.max_grade"
                    :min="institution.min_grade"
                    type="number"
                    :class="[
                      'm-0 p-0 w-100 text-center',
                      { 'border-danger': isInputInvalid(student) },
                    ]"
                    :style="{
                      borderWidth: isInputInvalid(student) ? '2px' : '1px',
                    }"
                    :value="institution.min_grade"
                    @input="(value) => validateFinalGradeInput(student, value)"
                  >
                  </b-form-input>
                  <div class="d-flex justify-content-center">
                    <b-icon
                      class="ml-1 mt-2 style-icon-btn"
                      v-b-tooltip.v-secondary.noninteractive.hover.top="
                        $t('reusable.common.save')
                      "
                      icon="save2"
                      @click="patchStudentScore(student)"
                    ></b-icon>
                    <b-icon
                      v-if="student && student.section_grade == null"
                      class="ml-1 mt-2 style-icon-btn"
                      v-b-tooltip.v-secondary.noninteractive.hover="
                        $t('dashboard.dashboardView.useAutomaticScore') +
                        ': ' +
                        +(
                          Math.round(Number(institution.min_grade) + 'e+2') +
                          'e-2'
                        )
                      "
                      icon="arrow-repeat"
                      @click="patchAutomatedGrade(student)"
                    ></b-icon>
                  </div>
                </div>
              </span>
            </td>
            <td
              v-if="student && student.section_grade && allows_crud"
              class="vertical-align-td"
            >
              <!-- Modo Edición -->
              <span>
                <div>
                  <b-form-input
                    :key="
                      student.id + '-' + (student_key_resets[student.id] || 0)
                    "
                    :ref="
                      'student:' +
                      student.id +
                      'final_evaluation:' +
                      student.section_grade.id +
                      'section:' +
                      section.id
                    "
                    v-b-tooltip.v-secondary.noninteractive.hover="
                      isInputInvalid(student) ? getValidationMessage() : ''
                    "
                    size="sm"
                    step="0.1"
                    :max="institution.max_grade"
                    :min="institution.min_grade"
                    type="number"
                    :class="[
                      'm-0 p-0 w-100 text-center',
                      {
                        'border-danger': isInputInvalid(student),
                      },
                    ]"
                    :style="{
                      borderWidth: isInputInvalid(student) ? '2px' : '1px',
                    }"
                    :value="
                      student.section_grade.final_grade != null
                        ? student.section_grade.final_grade.toFixed(1)
                        : student.section_grade.manual_grade != null
                        ? student.section_grade.manual_grade.toFixed(1)
                        : student.section_grade.automated_grade != null
                        ? student.section_grade.automated_grade.toFixed(1)
                        : institution.min_grade
                    "
                    @input="(value) => validateFinalGradeInput(student, value)"
                  >
                  </b-form-input>
                  <div class="d-flex justify-content-center">
                    <b-icon
                      class="ml-1 mt-2 style-icon-btn"
                      v-b-tooltip.v-secondary.noninteractive.hover.top="
                        $t('reusable.common.save')
                      "
                      icon="save2"
                      @click="patchStudentScore(student)"
                    ></b-icon>
                    <b-icon
                      v-if="student && student.section_grade"
                      class="ml-1 mt-2 style-icon-btn"
                      v-b-tooltip.v-secondary.noninteractive.hover="
                        $t('dashboard.dashboardView.useAutomaticScore') +
                        ': ' +
                        +(
                          Math.round(
                            Number(student.section_grade.automated_grade) +
                              'e+2'
                          ) + 'e-2'
                        )
                      "
                      icon="arrow-repeat"
                      @click="patchAutomatedGrade(student)"
                    ></b-icon>
                  </div>
                </div>
              </span>
            </td>
          </tr>
          <tr v-if="studentList.length == 0">
            <td :colspan="rowColspan()">
              {{ $t("dashboard.dashboardView.noDataMatchesSearch") }}
            </td>
          </tr>
        </b-tbody>
      </b-table-simple>

      <GradeTableRanges
        :grade_ranges="grade_ranges"
        :grade_ranges_groups="grade_ranges_groups"
        :grade_range_group_id="career_range_group"
        @selected_range_group="(value) => (selected_range_group = value)"
      ></GradeTableRanges>
      <!-- :total_grades="totalGrades" -->
      <div class="info mb-3">
        <h5 class="info-title">
          <b-icon icon="info-circle"></b-icon
          >{{ $t("dashboard.dashboardView.symbologyMeanings") }}
        </h5>
        <div class="info-container">
          <div class="d-flex" v-if="allows_crud">
            <b-icon icon="save2" class="mr-1"></b-icon>
            <p>
              {{
                $t("dashboard.dashboardView.saveGradeDescription", {
                  entity: $getVisibleNames(
                    "evaluations2.test",
                    false,
                    "Instrumento"
                  ),
                })
              }}
            </p>
          </div>
          <div class="d-flex mt-2" v-if="allows_crud">
            <b-icon icon="arrow-repeat" class="mr-1"></b-icon>
            <p>
              {{
                $t("dashboard.dashboardView.updateAutomaticScoreDescription")
              }}
            </p>
          </div>
          <!-- <div v-if="allows_crud">
          <div class="d-flex mt-1 ml-4">
            <b-icon icon="arrow-repeat" class="mt-1 mr-1"></b-icon>
            <strong> Evaluación:</strong> Aplica la suma de puntajes de los
            instrumentos asociados.
          </div>
          <div class="d-flex mt-1 ml-4">
            <b-icon icon="arrow-repeat" class="mt-1 mr-1"></b-icon>
            <strong> Instrumento:</strong> Aplica la suma de puntajes de las
            preguntas asociadas
          </div>
        </div> -->
          <div>
            <div class="d-flex mt-2">
              <b-icon icon="cloud-arrow-up" class="mr-1" scale="1"></b-icon>
              <p>
                {{
                  $t("dashboard.dashboardView.publishScoreDescription", {
                    entity: $getVisibleNames(
                      "evaluations2.test",
                      false,
                      "Instrumento"
                    ),
                  })
                }}
              </p>
            </div>
            <div class="d-flex mt-2">
              <b-icon icon="cloud-download" class="mr-1" scale="1"></b-icon>
              <p>
                {{
                  $t("dashboard.dashboardView.withdrawPublicationDescription", {
                    entity: $getVisibleNames(
                      "evaluations2.test",
                      false,
                      "Instrumento"
                    ),
                  })
                }}
              </p>
            </div>
            <!-- <div class="d-flex mt-1">
            <b-icon icon="journal-text" class="mr-1"></b-icon>
            <p>Permite al docente publicar todas las notas simultáneamente.</p>
          </div> -->
            <!-- <div class="d-flex mt-1">
            <b-icon icon="eye"></b-icon>
            <b-icon icon="eye-slash"></b-icon>
            <span class="ml-2">
              Indica si la nota está publicada para el estudiante.
            </span>
          </div> -->

            <div class="d-flex mt-2">
              <b-icon
                icon="lightbulb"
                class="mr-1 margin-icon-symbology"
              ></b-icon>
              <p>
                {{
                  $t("dashboard.dashboardView.indicatesAutomaticScore", {
                    entity: $getVisibleNames(
                      "evaluations2.test",
                      false,
                      "Instrumento"
                    ),
                  })
                }}
              </p>
            </div>

            <div class="d-flex mt-2">
              <b-icon
                icon="journal-check"
                class="margin-icon-symbology"
              ></b-icon>
              <b-icon
                icon="journal-x"
                class="mr-2 margin-icon-symbology"
              ></b-icon>
              <p>
                {{
                  $t("dashboard.dashboardView.indicatesStudentCannotAnswer", {
                    entity: $getVisibleNames(
                      "evaluations2.test",
                      false,
                      "Instrumento"
                    ),
                  })
                }}
              </p>
            </div>
            <div class="d-flex mt-2">
              <b-icon
                icon="person-x"
                scale="1.2"
                class="mr-1 margin-icon-symbology"
              ></b-icon>
              <p class="ml-1">
                {{
                  $t("dashboard.dashboardView.indicatesStudentCannotAnswer", {
                    entity: $getVisibleNames(
                      "evaluations2.test",
                      false,
                      "Instrumento"
                    ),
                  })
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast, showLoadingAlert, downloadFileFromResponse } from "@/utils/utils";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "NewFullGradingView",
  components: {
    NewMatterPresentation: () =>
      import(
        "@/components/teaching/MatterProgram/Components/NewMatterPresentation.vue"
      ),
    GradeTableRanges: () => import("./GradeTableRanges"),
    NewFullGradingInputComponent: () =>
      import("./NewFullGradingInputComponent"),
    // GenericGraphic: () => import("@/components/reusable/GenericGraphic"),
    vSelect,
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
    GenericButtonDownload: () =>
      import("@/components/reusable/Buttons/GenericButtonDownload"),
  },
  props: {},
  data() {
    return {
      section_id: Number(this.$route.params.section_id),
      current_section: null,
      input_search: "",
      displayList: [],
      check_instrument: [],
      selected_types: [],
      evaluationTypesList: [],
      grade_ranges: [],
      grade_ranges_groups: [],
      selected_range_group: null,
      career_range_group: null,
      key_reset_input: 0,
      student_key_resets: {},
      invalidInputs: {},
      disabled_button_excel: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      institution: "getInstitution",
      sections: names.SECTIONS,
      egress_profile_matters: names.EGRESS_PROFILES_MATTERS,
      evaluationTypes: names.NEW_EVALUATION_TYPES,
      matters: names.MATTERS,
    }),
    breadcrumb_items() {
      let breadcrumb = [
        { text: this.$t("reusable.breadcrumb.home"), to: "/home" },
      ];
      if (this.user && !this.user.group_names.includes("PROFESORES")) {
        breadcrumb.push({
          text: this.$getVisibleNames(
            "manual.administrar_seccion",
            true,
            "Administrar Secciones"
          ),
          to: "/section/form",
        });
      }
      breadcrumb.push({
        text: this.$t("dashboard.dashboardView.gradesRecordOfThe", {
          entity: this.$getVisibleNames("teaching.section", false, "Sección"),
        }),
        dropdownOptions: [
          ...(this.institution && this.institution.internal_use_id == "duoc.cl"
            ? ["showPdaView"]
            : ["showMatterProgram"]),
          "showAssessmentStudents",
          "showEvaluatorEvaluations",
          "showAssessmentRangeSectionView",
          "showProfessorProgressionReportView",
        ],
      });
      return breadcrumb;
    },
    section() {
      if (!this.section_id) return null;
      return this.sections.find((x) => x.id == this.section_id);
    },
    filterEvaluation() {
      if (!this.current_section) return [];
      else {
        if (this.selected_types.length > 0)
          return this.current_section.evaluations.filter((x) =>
            this.selected_types.includes(x.type)
          );
        else return this.current_section.evaluations;
      }
    },
    egress_profile_matter() {
      if (this.section && this.section.egress_profile_matter != null)
        return this.egress_profile_matters.find(
          (x) => x.id == this.section.egress_profile_matter
        );
      else return null;
    },
    matter() {
      if (this.section && this.section.matter != null)
        return this.matters.find((x) => x.id == this.section.matter);
      else if (
        this.egress_profile_matter &&
        this.egress_profile_matter.matter != null
      )
        return this.matters.find(
          (x) => x.id == this.egress_profile_matter.matter
        );
      else return null;
    },
    // TODO: cambiar cuando se agrege un permiso especial para poder cambiar las notas de una sección.
    has_grading_permission() {
      const has_permission = this.$hasPermissions(
        "evaluations2.change_evaluateeevaluation"
      );
      if (
        this.section &&
        this.section.is_active &&
        this.user &&
        (this.section.professors.includes(this.user.id) ||
          this.user.is_superuser ||
          this.user.group_names.includes(
            "DIRECTIVOS ACADÉMICOS INSTITUCIONALES"
          ))
      ) {
        this.$store.commit(names.MUTATE_VISIBILITY_CRUD, true);
        return has_permission;
      } else {
        this.$store.commit(names.MUTATE_VISIBILITY_CRUD, false);
        return false;
      }
    },
    allows_crud() {
      if (!this.has_grading_permission) return false;
      // El IPSS no puede editar las notas manualmente desde esta vista,
      // solo puede publicar/despublicar de forma masiva (ver can_publish_grades).
      if (this.institution && this.institution.internal_use_id == "ipss.cl") {
        return false;
      }
      return true;
    },
    can_publish_grades() {
      return this.has_grading_permission;
    },
    studentList() {
      if (!this.current_section) return [];
      if (this.input_search == "") return this.current_section.students;
      return this.current_section.students.filter((x) =>
        this.$filtered(
          String(x.first_name + " " + x.last_name),
          String(this.input_search)
        )
      );
    },
    gradeRangesList() {
      if (this.selected_range_group != null)
        return this.grade_ranges.filter(
          (x) => x.grade_range_group == this.selected_range_group
        );
      else return this.grade_ranges;
    },
  },
  methods: {
    validateFinalGradeInput(student, value) {
      const numValue = parseFloat(value);
      const key =
        student.section_grade == null
          ? `student:${student.id}section:${this.section.id}`
          : `student:${student.id}final_evaluation:${student.section_grade.id}section:${this.section.id}`;

      if (
        isNaN(numValue) ||
        numValue < this.institution.min_grade ||
        numValue > this.institution.max_grade
      ) {
        this.$set(this.invalidInputs, key, true);
      } else {
        this.$set(this.invalidInputs, key, false);
      }
    },
    isInputInvalid(student) {
      const key =
        student.section_grade == null
          ? `student:${student.id}section:${this.section.id}`
          : `student:${student.id}final_evaluation:${student.section_grade.id}section:${this.section.id}`;
      return this.invalidInputs[key] === true;
    },
    getValidationMessage() {
      return this.$t("dashboard.dashboardView.gradeMustBeBetween", {
        min: this.institution.min_grade,
        max: this.institution.max_grade,
      });
    },
    patchStudentScore(student) {
      if (student) {
        let score = 0;
        if (student.section_grade == null) {
          score = parseFloat(
            this.$refs[
              "student:" + student.id + "section:" + this.section.id
            ][0].localValue
          );
        }
        if (student.section_grade != null) {
          score = parseFloat(
            this.$refs[
              "student:" +
                student.id +
                "final_evaluation:" +
                student.section_grade.id +
                "section:" +
                this.section.id
            ][0].localValue
          );
        }
        if (
          score >= this.institution.min_grade &&
          score <= this.institution.max_grade
        ) {
          if (student.section_grade == null) {
            this.$restful
              .Post("/evaluations2/evaluatee-section/", {
                manual_grade: score,
                evaluatee: student.id,
                section: this.section.id,
              })
              .then(() => {
                this.handleScoreChange({ student_id: student.id });
              });
          } else {
            this.$restful
              .Patch(
                `/evaluations2/evaluatee-section/${student.section_grade.id}/`,
                {
                  manual_grade: score,
                }
              )
              .then(() => {
                this.handleScoreChange({ student_id: student.id });
              });
          }
        } else {
          this.$swal.fire({
            icon: "warning",
            title: this.$t("dashboard.dashboardView.validation"),
            text: this.getValidationMessage(),
          });
        }
      }
    },
    patchAutomatedGrade(student) {
      if (student.section_grade == null) {
        this.$restful
          .Post("/evaluations2/evaluatee-section/", {
            manual_grade: null,
            evaluatee: student.id,
            section: this.section.id,
          })
          .then(() => {
            this.handleScoreChange({ student_id: student.id });
          });
      } else {
        this.$restful
          .Patch(
            `/evaluations2/evaluatee-section/${student.section_grade.id}/`,
            {
              manual_grade: null,
            }
          )
          .then(() => {
            this.handleScoreChange({ student_id: student.id });
          });
      }
    },
    getGradeRange() {
      this.$restful.GetAll("/evaluations2/grade-range-group/").then((response) => {
        this.grade_ranges_groups = response;
      });
      this.$restful.GetAll("/evaluations2/grade-range/").then((response) => {
        this.grade_ranges = response;
      });
    },
    gradeRangeBackground(grade) {
      for (let range of this.gradeRangesList) {
        if (range.include_start_grade && range.include_finish_grade) {
          if (grade >= range.start_grade && grade <= range.finish_grade) {
            return range.color;
          }
        } else if (range.include_start_grade && !range.include_finish_grade) {
          if (grade >= range.start_grade && grade < range.finish_grade) {
            return range.color;
          }
        } else if (!range.include_start_grade && range.include_finish_grade) {
          if (grade > range.start_grade && grade <= range.finish_grade) {
            return range.color;
          }
        } else {
          if (grade > range.start_grade && grade < range.finish_grade) {
            return range.color;
          }
        }
      }
      return null; // Retorna null si no encuentra ningún rango que coincida.
    },
    dropdownShouldOpen(VueSelect) {
      if (this.evaluationTypesList.length > 0) {
        return VueSelect.open;
      }
      return VueSelect.search.length !== 0 && VueSelect.open;
    },
    publishGradeToAllEvaluatees(instrument_id, set_to = true) {
      this.$restful
        .Post(`/evaluations2/set_evaluatee_tests_to_is_published/`, {
          test: instrument_id,
          set_to: set_to,
        })
        .then(() => {
          toast(
            this.$t("dashboard.dashboardView.instrumentPublished", {
              entity: this.$getVisibleNames(
                "evaluations2.test",
                false,
                "Instrumento"
              ),
            })
          );
          this.resetTable();
        });
    },
    getStudentIdsInTest(tests) {
      let student_ids = [];
      tests.forEach((x) => {
        student_ids = student_ids.concat(x.evaluatees);
      });
      return [...new Set(student_ids)];
    },
    showColumn(instruments) {
      let list = [];
      const instrument_ids = this.displayList
        .filter((x) => this.check_instrument.includes(x.index))
        .map((object) => object.id);
      list = instruments.filter((x) => instrument_ids.includes(x.id));
      return list;
    },
    getSectionsTeacher(reset_check_instrument = true) {
      if (this.user && !this.user.group_names.includes("ESTUDIANTES")) {
        this.$store
          .dispatch(names.FETCH_SECTION, this.section_id)
          .then((section) => {
            if (section.egress_profile_matter != null) {
              this.$store
                .dispatch(
                  names.FETCH_EGRESS_PROFILE_MATTER,
                  section.egress_profile_matter
                )
                .then((egress_profile_matter) => {
                  this.$store
                    .dispatch(
                      names.FETCH_EGRESS_PROFILE,
                      egress_profile_matter.egress_profile
                    )
                    .then((egress_profile) => {
                      this.$store
                        .dispatch(names.FETCH_CAREER, egress_profile.career)
                        .then((career) => {
                          if (career.chosen_grade_range_group != null) {
                            this.career_range_group =
                              career.chosen_grade_range_group;
                            this.selected_range_group =
                              career.chosen_grade_range_group;
                          }
                        });
                    });
                  this.$store.dispatch(
                    names.FETCH_MATTER,
                    egress_profile_matter.matter
                  );
                });
            } else if (section.matter != null) {
              this.$store.dispatch(names.FETCH_MATTER, section.matter);
            }
          });
        this.displayList = [];
        if (reset_check_instrument) this.check_instrument = [];
        this.$restful
          .Get(`/evaluations2/section_grades/?section=${this.section_id}`)
          .then((response) => {
            this.current_section = response;
            const evaluation_types_ids = [
              ...new Set(response.evaluations.map((x) => x.type)),
            ];
            this.selected_types = evaluation_types_ids;
            this.evaluationTypesList = this.evaluationTypes.filter((x) =>
              evaluation_types_ids.includes(x.id)
            );
            let index = 0;
            response.evaluations.forEach((evaluation) => {
              evaluation.tests.forEach((instrument) => {
                this.displayList.push({
                  index: index,
                  id: instrument.id,
                  title: instrument.title,
                  evaluation_id: evaluation.id,
                });
                if (
                  evaluation.weighing > 0 &&
                  instrument.weighing > 0 &&
                  reset_check_instrument
                )
                  this.check_instrument.push(index);
                index += 1;
              });
            });
          });
      }
    },
    redirectEvaluatorView(evaluation_id, instrument_id = null) {
      this.$router.push({
        name: "EvaluatorView",
        params: {
          evaluation_id: evaluation_id,
          instrument_id: instrument_id,
        },
      });
    },
    getFinalGrade(section_grade) {
      let final_grade =
        section_grade.final_grade != null
          ? section_grade.final_grade
          : section_grade.manual_grade != null
          ? section_grade.manual_grade
          : section_grade.automated_grade != null
          ? section_grade.automated_grade
          : this.institution.min_grade;
      return parseFloat(final_grade.toFixed(1));
    },
    rowColspan() {
      let count = 2;
      if (this.current_section) {
        this.filterEvaluation.forEach((x) => {
          count += x.tests.length + 1;
        });
        return count;
      }
      return count;
    },
    updateAutomaticScoreAllTest(test_id) {
      if (!isNaN(test_id) && this.section) {
        showLoadingAlert(
          this.$t("dashboard.dashboardView.updatingScoresOf", {
            entity: this.$getVisibleNames(
              "evaluations2.test",
              false,
              "Instrumento"
            ),
          }),
          this.$t("reusable.common.pleaseWait")
        );
        this.$restful
          .Patch(`/evaluations2/test/${test_id}/reset_scores/`, {
            section: this.section.id,
          })
          .then(() => {
            this.$swal.close();
            this.getSectionsTeacher(false);
          })
          .catch(() => {
            this.$swal.close();
            this.$swal.fire(
              this.$t("reusable.common.error"),
              this.$t("dashboard.dashboardView.problemUpdatingInstruments", {
                entity: this.$getVisibleNames(
                  "evaluations2.test",
                  true,
                  "Instrumentos"
                ),
              }),
              "error"
            );
          });
      }
    },
    updateAutomaticScoreAllEvaluation(evaluation_id) {
      if (!isNaN(evaluation_id) && this.section) {
        showLoadingAlert(
          this.$t("dashboard.dashboardView.updatingEvaluationGrades"),
          this.$t("reusable.common.pleaseWait")
        );
        this.$restful
          .Patch(`/evaluations2/evaluation/${evaluation_id}/reset_grades/`, {
            section: this.section.id,
          })
          .then(() => {
            this.$swal.close();
            this.getSectionsTeacher(false);
          })
          .catch(() => {
            this.$swal.close();
            this.$swal.fire(
              this.$t("reusable.common.error"),
              this.$t("dashboard.dashboardView.problemUpdatingGrades"),
              "error"
            );
          });
      }
    },
    updateAutomaticScoreAllSection() {
      if (this.section) {
        showLoadingAlert(
          this.$t("dashboard.dashboardView.updatingFinalGradeOf", {
            entity: this.$getVisibleNames("teaching.section", false, "Sección"),
          }),
          this.$t("reusable.common.pleaseWait")
        );
        this.$restful
          .Patch(`/teaching/section/${this.section.id}/reset_grades/`)
          .then(() => {
            this.$swal.close();
            this.getSectionsTeacher(false);
          })
          .catch(() => {
            this.$swal.close();
            this.$swal.fire(
              this.$t("reusable.common.error"),
              this.$t("dashboard.dashboardView.problemUpdatingFinalGrade"),
              "error"
            );
          });
      }
    },
    async handleScoreChange({ student_id }) {
      const response = await this.$restful.Get(
        `/evaluations2/section_grades/?section=${this.section_id}`
      );
      const updatedStudent = response.students.find((s) => s.id == student_id);
      if (updatedStudent) {
        const idx = this.current_section.students.findIndex(
          (s) => s.id == student_id
        );
        if (idx !== -1) {
          this.$set(this.current_section.students, idx, updatedStudent);
        }
      }
      this.$set(
        this.student_key_resets,
        student_id,
        (this.student_key_resets[student_id] || 0) + 1
      );
      toast(this.$t("dashboard.dashboardView.gradeUpdated"));
    },
    resetTable() {
      this.key_reset_input++;
      this.getSectionsTeacher(false);
      toast(this.$t("dashboard.dashboardView.gradeUpdated"));
    },
    DownloadExcel() {
      this.disabled_button_excel = true;
      showLoadingAlert(
        this.$t("reusable.common.generatingExcel"),
        this.$t("reusable.common.pleaseWaitFile")
      );
      const visible_test_ids = this.displayList
        .filter((x) => this.check_instrument.includes(x.index))
        .map((x) => x.id);
      this.$restful
        .Post(
          `/evaluations2/excel_section_grades/`,
          {
            section: this.section_id,
            evaluation_ids: this.filterEvaluation.map((x) => x.id),
            test_ids: visible_test_ids,
          },
          {
            responseType: "blob",
          }
        )
        .then((response) => {
          this.$swal.close();
          if (response) {
            downloadFileFromResponse(
              response,
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              () => {
                this.disabled_button_excel = false;
              },
              () => {
                this.disabled_button_excel = false;
                this.$swal.fire(
                  this.$t("reusable.common.error"),
                  this.$t("reusable.common.downloadError"),
                  "error"
                );
              }
            );
          } else {
            this.disabled_button_excel = false;
            this.$swal.fire(
              this.$t("reusable.common.error"),
              this.$t("reusable.common.generateError"),
              "error"
            );
          }
        })
        .catch(() => {
          this.disabled_button_excel = false;
          this.$swal.close();
          this.$swal.fire(
            this.$t("reusable.common.error"),
            this.$t("reusable.common.problemGenerating"),
            "error"
          );
        });
    },
  },
  mounted() {},
  watch: {},
  created() {
    this.getGradeRange();
    this.$store.dispatch(names.FETCH_NEW_EVALUATION_TYPES).then(() => {
      this.getSectionsTeacher();
    });
  },
};
</script>

<style scoped>
.final-grade {
  width: 6% !important;
}
.min-width-input {
  min-width: 72px;
}
.vertical-align-td {
  vertical-align: middle;
}
.v-select-class >>> .vs__selected {
  background-color: var(--kl-chip-solid-bg);
  color: var(--kl-chip-solid-text) !important;
}
.style-icon-btn {
  transition: background-color 0.3s, transform 0.2s, box-shadow 0.2s;
  border-radius: 50%;
  cursor: pointer;
}
.style-icon-btn:hover {
  background-color: #ececec88; /* Color de fondo al pasar el mouse */
  border-radius: 50%;
  transform: scale(1.1); /* Aumenta ligeramente el tamaño al pasar el mouse */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}
.margin-icon-symbology {
  margin-top: -2px;
}
.flex-container {
  display: flex;
  margin-bottom: 1em;
}
.div-href {
  color: white;
}
.div-href:hover {
  color: #4dafff !important;
  text-decoration: underline;
  cursor: pointer;
}
.column-selector {
  display: flex;
  align-items: center;
}
.col-display-1 {
  margin-left: 1em;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  background-color: var(--kl-primary-button-color) !important;
  color: #fff;
}
.col-display-2 {
  min-width: 154px;
  margin-left: 1em;
  padding-left: 1rem;
  padding-right: 1rem;
  background-color: var(--kl-primary-button-color) !important;
  color: #fff;
}
.col-display-1:hover,
.col-display-2:hover {
  background-color: var(--kl-primary-button-hover-color) !important;
  transition: all 0.3s;
}
.badge-score-evaluation {
  background: white;
  color: black;
  padding-top: 5px;
  font-weight: bold;
  font-size: 8pt;
  letter-spacing: 0.3px;
  margin-left: 15px;
}
.table-current-section {
  font-size: var(--secondary-font-size);
}
.header-section {
  background-color: var(--kl-color-accent-medium) !important;
  color: white;
}
.report-ranges-caption {
  width: 100%;
  border: 1px solid var(--kl-border-soft);
  font-size: var(--secondary-font-size);
}
.report-ranges-header {
  text-align: left;
  padding: 0.5em 1em;
  padding-left: 2em;
  background: var(--kl-color-accent-medium) !important;
  color: white;
  font-weight: bold;
}
.report-range {
  display: flex;
  margin-left: 0;
  padding: 0.5em 4em;
  border-bottom: 1px solid var(--kl-border-soft);
}
.range-label {
  font-weight: bold;
  text-align: left;
  width: 25%;
}
.report-range .range-value-red {
  background: #f19292;
  width: 25%;
}
.report-range .range-value-yellow {
  background: #fff1ac;
  width: 25%;
}
.report-range .range-value-green {
  background: #bbeaa6;
  width: 25%;
}
.info {
  background-color: var(--kl-color-accent-medium) !important;
  border-radius: 10px;
  margin-top: 1.8%;
  padding: 0 2% 2% 2%;
}
.info-container {
  background-color: var(--kl-surface);
  padding: 1rem;
  border-radius: 10px;
}
.info-title {
  text-align: left;
  padding-top: 1%;
  margin-bottom: 2%;
  color: white !important;
}
.underline-style {
  text-decoration: underline;
}
.info-title .b-icon {
  margin-right: 1%;
}
.info .b-icon {
  max-width: 2rem;
  margin-left: 1%;
}
.info p {
  text-align: left;
  margin-top: -0.4%;
  margin-bottom: 0;
}
.border-danger {
  border-color: #dc3545 !important;
}
</style>